import { useQuery } from '@tanstack/react-query';
import { httpsCallable } from '../lib/firebase/authReadyCallable';
import { functions } from '../config/firebase';
import { useOrganization } from '../contexts/OrganizationContext';
import { isSoftDeleted } from '../lib/appsheet/softDelete';
import { readGeneratorPin } from '../lib/fieldService/generatorPin';
import { readGalleryPreview } from '../lib/fieldService/galleryPreview';
import type { GalleryPreviewSummary } from '../types/fieldService/generatorPhotos';

/** Coerce a Firestore Timestamp or string to a display string. */
function coerceDateToString(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  }
  return '';
}

/**
 * The client contact the list row shows for a unit (ISS-463).
 *
 * Joined SERVER-side by `listGenerators` from `appsheet_shared_contacts` — the
 * generator record itself carries only contact IDs, never a phone or an email.
 * Absent when the unit links no live, same-org contact.
 */
export interface GeneratorContact {
  name: string;
  role?: string;
  phone?: string;
  email?: string;
  /**
   * False when this is NOT the unit's designated primary contact — the server
   * promoted a member of the unit's `Contacts` list because the primary was blank
   * or had no phone/email. That list can hold a property manager or a vendor, so
   * the UI labels it rather than presenting them as the client.
   */
  isPrimary?: false;
}

export interface Generator {
  id: string;
  generatorId: string;
  clientName: string;
  siteName?: string;
  /** Customer's own name for this specific unit (AppSheet 'Client Name Sub-Account'), e.g. "RNB Gen #1". */
  unitName?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  branch?: string;
  status?: string;
  /** Comma/line-separated list of recurring service types on this unit's schedule (denormalized from AppSheet). */
  recurringServiceList?: string;
  // Generator specs
  make?: string;
  model?: string;
  serialNumber?: string;
  kw?: number;
  voltage?: string;
  fuel?: string;
  phase?: string;
  // Engine specs
  engineMake?: string;
  engineModel?: string;
  engineSerial?: string;
  // Transfer switch
  transferSwitchMake?: string;
  transferSwitchModel?: string;
  // Dates
  installDate?: string;
  lastServiceDate?: string;
  warrantyStatus?: string;
  // Enclosure
  enclosure?: string;
  enclosureSize?: string;
  notes?: string;
  /** Client contact for this unit, joined server-side. See `GeneratorContact`. */
  primaryContact?: GeneratorContact;
  /**
   * The unit's pin point, when the list callable returns one (`_opgUnit.pin`
   * lat/lng/source only, never its history). Absent = no pin.
   */
  pin?: { lat: number; lng: number };
  /** The unit's gallery preview (`_opgUnit.gallery`, three leaves). Absent = no photos. */
  gallery?: GalleryPreviewSummary;
}

/**
 * Exported so the DETAIL hook can seed from this exact cache entry. It reads
 * with `getQueryData`, which is an EXACT match — when this key gained its
 * orgId segment, a hardcoded `['generators','list']` on the other side
 * silently stopped matching and the detail page lost its instant paint with
 * no error. A shared factory is the only thing that keeps the two in step.
 */
export const generatorListKeys = {
  all: ['generators'] as const,
  // orgId terminates the key: switchOrganization does not clear the React
  // Query cache, so a shared key would serve one org's catalogue to another.
  list: (orgId?: string) => [...generatorListKeys.all, 'list', orgId] as const,
};

function parseKw(val?: string | number): number | undefined {
  if (val === undefined || val === null || val === '') return undefined;
  if (typeof val === 'number') return val;
  const n = parseFloat(val);
  return isNaN(n) ? undefined : n;
}

/** One projected row as the callable returns it — AppSheet PascalCase keys. */
type RawGeneratorRow = Record<string, unknown> & { id: string; _deleted?: unknown };

/** `{ pin }` when the row carries a usable pin, else nothing (no `pin: undefined` key). */
function pinPoint(raw: RawGeneratorRow): { pin?: { lat: number; lng: number } } {
  const pin = readGeneratorPin(raw);
  return pin ? { pin: { lat: pin.lat, lng: pin.lng } } : {};
}

/** `{ gallery }` when the row carries a usable preview, else nothing (no `gallery: undefined` key). */
function galleryPreview(raw: RawGeneratorRow): { gallery?: GalleryPreviewSummary } {
  const gallery = readGalleryPreview(raw);
  return gallery ? { gallery } : {};
}

/**
 * Coerce an AppSheet cell to a display string.
 *
 * Firestore values arrive as `unknown` through the callable. AppSheet stores
 * most columns as strings but not all (KW is numeric on some rows), so this
 * stringifies rather than assuming, and maps null/undefined to '' — the empty
 * value every consumer of `Generator` already expects. Objects (a stray
 * Timestamp or array in a projected column) also collapse to '' rather than
 * rendering as "[object Object]" in the table.
 */
const str = (v: unknown): string =>
  typeof v === 'string' ? v : typeof v === 'number' || typeof v === 'boolean' ? String(v) : '';

/**
 * Narrow the callable's `unknown` contact block. The wire is untyped, so a row
 * whose block lost its shape must be dropped rather than rendered as `[object
 * Object]` — the same reason `str()` above refuses objects.
 */
function isGeneratorContact(value: unknown): value is GeneratorContact {
  if (typeof value !== 'object' || value === null) return false;
  const c = value as Record<string, unknown>;
  const optionalString = (v: unknown) => v === undefined || typeof v === 'string';
  return (
    typeof c.name === 'string' &&
    optionalString(c.role) &&
    optionalString(c.phone) &&
    optionalString(c.email) &&
    // Any boolean: a future server that stamps `isPrimary: true` must not make every
    // primary contact vanish silently. Only `false` renders the Linked badge.
    (c.isPrimary === undefined || typeof c.isPrimary === 'boolean')
  );
}

interface GeneratorListResponse {
  generators: RawGeneratorRow[];
  count: number;
}

/**
 * The generator catalogue for the Field Service list page.
 *
 * Reads through `fieldService_listGenerators` rather than Firestore directly.
 * The collection carries 92 fields per document (~10 MB across 3,220 rows) and
 * this table renders 11 of them; the Web SDK cannot project fields, so the
 * whole 10 MB used to cross the wire on every mount and the page sat on
 * "Loading..." for 20-30s (#3493). The callable uses the Admin SDK's
 * `select()` to return ~1 MB instead.
 *
 * Sorting moved client-side with it: `GeneratorsPage` already re-sorts by its
 * own column state, so the server `orderBy('Client Name')` was only ever
 * setting an initial order the page immediately overrode.
 */
export function useGenerators(options?: { enabled?: boolean }) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId;

  const query = useQuery({
    queryKey: generatorListKeys.list(orgId),
    queryFn: async (): Promise<Generator[]> => {
      const call = httpsCallable<{ organizationId: string }, GeneratorListResponse>(
        functions,
        'fieldService_listGenerators'
      );
      // Sending organizationId makes extractCallableContext VERIFY membership.
      // Omitting it silently falls back to the profile org, which diverges from
      // the UI-selected org this cache key is built on.
      const { data } = await call({ organizationId: orgId! });
      const rows = (data?.generators ?? []) as RawGeneratorRow[];
      return rows
        .filter((raw) => !isSoftDeleted(raw))
        .map((raw) => ({
          id: raw.id,
          generatorId: str(raw['Generator ID']),
          // "Client Name" is blank on a small slice of AppSheet-mirrored rows
          // (measured: exactly 10 of 3,220, 2026-09-08). "Management Company"
          // is documented as a DIFFERENT field (the property manager, not
          // necessarily the billed customer) but for every one of those 10
          // rows it holds the same short company name that "Client Full Name"
          // (the dictionary's fillRate:1 "full customer name" field) leads
          // with too — so it's preferred first for the clean short form, with
          // "Client Full Name" as a last-resort fallback (verbose but always
          // populated) for any row where even "Management Company" is blank.
          clientName: str(raw['Client Name']) || str(raw['Management Company']) || str(raw['Client Full Name']),
          siteName: str(raw['Site Name']),
          unitName: str(raw['Client Name Sub-Account']),
          address: str(raw['Generator Address']),
          city: str(raw['Generator Address City']),
          state: str(raw['Generator Address State']),
          zip: str(raw['Equipment Address Zip']),
          branch: str(raw['Branch']),
          status: str(raw['Status']),
          recurringServiceList: str(raw['Recurring Service List']),
          make: str(raw['Generator Make']),
          model: str(raw['Generator Model']),
          serialNumber: str(raw['Generator Serial Number']),
          kw: parseKw(raw['Generator KW'] as string | number | undefined),
          voltage: str(raw['Voltage']),
          fuel: str(raw['Fuel']),
          phase: '', // Field not synced from AppSheet
          engineMake: str(raw['Engine Make']),
          engineModel: str(raw['Engine Model']),
          engineSerial: str(raw['Engine Serial Number']),
          transferSwitchMake: str(raw['ATS Make']),
          transferSwitchModel: str(raw['ATS Model']),
          installDate: coerceDateToString(raw['Install Date']),
          lastServiceDate: coerceDateToString(raw['Last Completed Service Date']),
          warrantyStatus: coerceDateToString(raw['Warranty Expires']),
          enclosure: '', // Field not synced from AppSheet
          enclosureSize: '', // Field not synced from AppSheet
          notes: str(raw['Generator Notes']),
          // Pass the server's joined block through as-is when it is a real
          // object. Spread-with-undefined would put an explicit `primaryContact:
          // undefined` key on every row, which reads the same but serializes
          // into the cache on 1,030 rows that have no contact.
          ...(isGeneratorContact(raw.primaryContact)
            ? { primaryContact: raw.primaryContact }
            : {}),
          ...pinPoint(raw),
          ...galleryPreview(raw),
        }))
        // Server no longer orders — the page's own sort takes over from here.
        .sort((a, b) => a.clientName.localeCompare(b.clientName));
    },
    enabled: !!orgId && (options?.enabled ?? true),
    staleTime: 1000 * 60 * 5,
  });

  // A disabled query reports isLoading:false (it is pending + idle), so
  // consumers gating their skeleton on isLoading would render "No generators
  // found" for the whole org-bootstrap window — a confident empty state over
  // a catalogue of 3,220. isPending stays true until real data arrives.
  return { ...query, isLoading: query.isPending };
}
